import { useState } from 'react';
import PageHero from '../components/PageHero';
import './Contact.css';

const FAQS = [
  { q: 'How can I volunteer with the Trust?', a: 'We welcome volunteers for field programmes, digital campaigns, event support, and professional skills. Fill out the contact form with "Volunteer" as the subject, or visit our Volunteer page.' },
  { q: 'How do you ensure transparency in your programmes?', a: 'We publish annual audited accounts and impact reports on our website. Every programme has tracked KPIs. We are also listed on GuideStar India with a Platinum rating.' },
  { q: 'Can my company run a CSR programme with you?', a: 'Absolutely. We have active CSR partnerships with several companies. We co-design programmes aligned to your CSR mandate, provide measurable outcomes, and handle all compliance reporting. Contact us at partnerships@shinepeopletrust.org.' },
];

const INFO_ITEMS = [
  {
    icon: '📍',
    iconBg: 'icon-bg-teal',
    title: 'Address',
    content: (
      <p className="contact-info-text">
        Papermills Road, Periyar Nagar<br />
        Chennai<br />
        Tamil Nadu, India
      </p>
    ),
  },
  {
    icon: '✉️',
    iconBg: 'icon-bg-blue',
    title: 'Email',
    content: (
      <p className="contact-info-text">
        <a href="mailto:info@shinepeopletrust.org">info@shinepeopletrust.org</a><br />
        <a href="mailto:partnerships@shinepeopletrust.org">partnerships@shinepeopletrust.org</a>
      </p>
    ),
  },
  {
    icon: '🕐',
    iconBg: 'icon-bg-purple',
    title: 'Office Hours',
    content: (
      <div className="contact-hours">
        <div className="contact-hour-row"><span>Mon – Fri</span><span>9:00 AM – 6:00 PM</span></div>
        <div className="contact-hour-row"><span>Saturday</span><span>10:00 AM – 2:00 PM</span></div>
        <div className="contact-hour-row"><span>Sunday</span><span>Closed</span></div>
      </div>
    ),
  },
];

function FaqItem({ faq }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item${open ? ' open' : ''}`}>
      <button
        className="faq-question"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        {faq.q}
        <span className="faq-chevron" aria-hidden="true">▼</span>
      </button>
      <div className="faq-answer" aria-hidden={!open}>{faq.a}</div>
    </div>
  );
}

export default function Contact() {
  return (
    <main id="main-content">
      <PageHero
        label="Reach Out"
        title="We'd Love to Hear From You"
        desc="Whether you want to volunteer, partner, or simply learn more about our work — our team is ready to connect."
        breadcrumb="Contact"
        bgImage="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=1600&h=600&q=80"
      />

      <section className="contact-main section" aria-label="Contact details and form">
        <div className="container">
          <div className="contact-grid">
            {/* Sidebar info */}
            <aside aria-label="Contact information">
              <div className="contact-info">
                {INFO_ITEMS.map(item => (
                  <div className="contact-info-card" key={item.title}>
                    <div className="contact-info-header">
                      <div className={`contact-info-icon ${item.iconBg}`} aria-hidden="true">{item.icon}</div>
                      <div className="contact-info-title">{item.title}</div>
                    </div>
                    {item.content}
                  </div>
                ))}

                {/* Social links */}
                <div className="contact-info-card">
                  <div className="contact-info-header">
                    <div className="contact-info-icon icon-bg-teal" aria-hidden="true">🌐</div>
                    <div className="contact-info-title">Follow Us</div>
                  </div>
                  <div className="contact-social-links" role="list" aria-label="Social media links">
                    {['f', '𝕏', 'in', '▶', '📸'].map((ic, i) => (
                      <button key={i} className="contact-social-link" role="listitem" aria-label={['Facebook', 'Twitter', 'LinkedIn', 'YouTube', 'Instagram'][i]}>{ic}</button>
                    ))}
                  </div>
                </div>

                {/* Map */}
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Papermills+Road%2C+Periyar+Nagar%2C+Chennai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-map"
                  aria-label="View office location on Google Maps — Periyar Nagar, Chennai"
                >
                  <span aria-hidden="true">🗺️</span>
                  <span className="contact-map-text">View on Google Maps</span>
                </a>
              </div>
            </aside>

            {/* Get in touch card */}
            <div className="contact-form-card">
              <h2 className="contact-form-title">Get in Touch</h2>
              <p className="contact-form-subtitle" style={{ marginBottom: '2rem' }}>
                Reach out to us directly using the contact details on this page. Our team responds within 1–2 business days.
              </p>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
                <a href="mailto:info@shinepeopletrust.org" className="btn btn-primary btn-lg">
                  Email Us
                </a>
              </div>

              {/* Images */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.25rem' }}>
                <div style={{ position: 'relative' }}>
                  <img
                    src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=400&h=200&q=80"
                    alt="Team collaborating in an office meeting"
                    style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '0.75rem', display: 'block' }}
                  />
                  <span style={{ position: 'absolute', bottom: '0.625rem', left: '0.625rem', background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: '0.75rem', fontWeight: 600, padding: '0.25rem 0.625rem', borderRadius: '0.375rem', backdropFilter: 'blur(4px)' }}>Our Team</span>
                </div>
                <div style={{ position: 'relative' }}>
                  <img
                    src="https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=400&h=200&q=80"
                    alt="Staff discussing programme plans"
                    style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '0.75rem', display: 'block' }}
                  />
                  <span style={{ position: 'absolute', bottom: '0.625rem', left: '0.625rem', background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: '0.75rem', fontWeight: 600, padding: '0.25rem 0.625rem', borderRadius: '0.375rem', backdropFilter: 'blur(4px)' }}>Planning</span>
                </div>
              </div>

              {/* Flex-fill image — stretches to fill remaining card height */}
              <div className="contact-img-fill">
                <img
                  src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=800&h=400&q=80"
                  alt="Community members gathered for a Shine People Trust programme"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="contact-faq section" aria-labelledby="faq-heading">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Quick Answers</span>
            <h2 className="section-title" id="faq-heading">Frequently Asked Questions</h2>
          </div>
          <div className="faq-list" role="list">
            {FAQS.map(faq => <FaqItem key={faq.q} faq={faq} />)}
          </div>
        </div>
      </section>
    </main>
  );
}
